import { useToast } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CopiedSourcingEventDto, useCopySourcingEventV2Mutation } from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { urls } from "../../urls";
import { InvitationsLeftOutModal } from "./InvitationsLeftOutModal";

export const useCopyProcurement = ({ onError }: { onError?: (e: unknown) => void } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger } = useApiError();
  const [copyEvent, { isLoading }] = useCopySourcingEventV2Mutation();
  const [copyWithInvitationsLeftOut, setCopyWithInvitationsLeftOut] = useState<CopiedSourcingEventDto | null>(null);
  const reportError = onError ?? trigger;

  const openCopy = (copied: CopiedSourcingEventDto) => navigate(urls.v2.sourcingEvents.edit.go(copied.event.id).info);

  const copy = async (eventId: string) => {
    try {
      const copied = await copyEvent({ eventId }).unwrap();
      if (copied.skippedInvitations > 0) {
        setCopyWithInvitationsLeftOut(copied);
        return;
      }
      openCopy(copied);
      if (copied.copiedInvitations > 0) {
        toast({
          status: "warning",
          duration: null,
          isClosable: true,
          title: t("The copy carries invitations"),
          description: t("The invitations from the original were copied. Review them before you publish."),
        });
      }
    } catch (e) {
      reportError(e);
    }
  };

  const modal = copyWithInvitationsLeftOut ? (
    <InvitationsLeftOutModal
      copied={copyWithInvitationsLeftOut}
      onOpenCopy={() => {
        setCopyWithInvitationsLeftOut(null);
        openCopy(copyWithInvitationsLeftOut);
      }}
    />
  ) : null;

  return { copy, isCopying: isLoading, modal };
};
